import { useState, type FormEvent, type DragEvent } from 'react'
import { useMutation, gql } from 'urql'
import { uploadFile, type UploadResult } from '../lib/file-upload'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { DateInput } from './ui/date-input'
import { Category, CATEGORY_DISPLAY, CATEGORY_EMOJIS } from '../lib/constants'
import { FileText, MapPin, Plus, Trash2, UploadCloud, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { FileUploader } from './file-uploader'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog'
import type { TransactionData } from './transaction-card'

const UPDATE_TRANSACTION_MUTATION = gql(`
  mutation UpdateTransaction($id: ID!, $merchant: String, $purchasedAt: String, $notes: String) {
    updateTransaction(id: $id, merchant: $merchant, purchasedAt: $purchasedAt, notes: $notes) { id }
  }
`)

const UPDATE_ITEM_MUTATION = gql(`
  mutation UpdateItemInTransaction($input: UpdateItemInput!) {
    updateItem(input: $input) { id name amount category merchant purchasedAt receiptUrl }
  }
`)

const ADD_ITEM_TO_TRANSACTION_MUTATION = gql(`
  mutation AddItemToTransaction($transactionId: ID!, $item: AddItemInput!) {
    addItemToTransaction(transactionId: $transactionId, item: $item) { id name amount category }
  }
`)

const DELETE_TRANSACTION_MUTATION = gql(`
  mutation DeleteTransaction($id: ID!) {
    deleteTransaction(id: $id)
  }
`)

interface EditableItem {
  id?: string
  name: string
  amount: string
  category: string
}

interface EditTransactionFormProps {
  transaction: TransactionData
  currencySymbol?: string
  onSuccess: () => void
}


export function EditTransactionForm({
  transaction,
  currencySymbol = '$',
  onSuccess,
}: EditTransactionFormProps) {
  const [merchant, setMerchant] = useState(transaction.merchant ?? '')
  const [purchasedAt, setPurchasedAt] = useState(transaction.purchasedAt.split('T')[0])
  const [notes, setNotes] = useState(transaction.notes ?? '')
  const [showNotes, setShowNotes] = useState(!!transaction.notes)
  const [items, setItems] = useState<EditableItem[]>(
    (transaction.items ?? []).map((i) => ({
      id: i.id,
      name: i.name,
      amount: i.amount?.toString() ?? '',
      category: i.category ?? 'OTHER',
    }))
  )

  const [receiptFile, setReceiptFile] = useState<File | null>(null)
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null)
  const [isReplacingReceipt, setIsReplacingReceipt] = useState(false)
  const [isDraggingOver, setIsDraggingOver] = useState(false)
  const [latitude, setLatitude] = useState<number | null>(transaction.latitude ?? null)
  const [longitude, setLongitude] = useState<number | null>(transaction.longitude ?? null)
  const [isLocating, setIsLocating] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [amountErrors, setAmountErrors] = useState<Record<number, string>>({})
  const [error, setError] = useState('')
  const [showDeleteTxConfirm, setShowDeleteTxConfirm] = useState(false)

  const [{ fetching: savingTx }, updateTransaction] = useMutation(UPDATE_TRANSACTION_MUTATION)
  const [{ fetching: savingItems }, updateItem] = useMutation(UPDATE_ITEM_MUTATION)
  const [{ fetching: addingItem }, addItemToTransaction] = useMutation(ADD_ITEM_TO_TRANSACTION_MUTATION)
  const [{ fetching: deletingTx }, deleteTransaction] = useMutation(DELETE_TRANSACTION_MUTATION)
  const saving = savingTx || savingItems || addingItem

  const currentReceiptUrl = transaction.receiptUrl

  const onFormDragOver = (e: DragEvent<HTMLDivElement>) => {
    if (e.dataTransfer.types.includes('Files')) { e.preventDefault(); setIsDraggingOver(true) }
  }
  const onFormDragLeave = (e: DragEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDraggingOver(false)
  }
  const onFormDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDraggingOver(false)
    const f = e.dataTransfer.files?.[0]
    if (f && (f.type.startsWith('image/') || f.type === 'application/pdf')) {
      setReceiptFile(f)
      setUploadResult(null)
      setIsReplacingReceipt(true)
    }
  }

  const handleLocation = () => {
    if (!('geolocation' in navigator)) return
    setIsLocating(true)
    navigator.geolocation.getCurrentPosition(
      (p) => { setLatitude(p.coords.latitude); setLongitude(p.coords.longitude); setIsLocating(false) },
      () => setIsLocating(false)
    )
  }

  const updateField = (index: number, field: keyof EditableItem, value: string) => {
    setItems((prev) => prev.map((item, i) => i === index ? { ...item, [field]: value } : item))
    if (field === 'amount') {
      setAmountErrors((prev) => { const n = { ...prev }; delete n[index]; return n })
    }
  }

  const toggleCategory = (index: number, cat: string) => {
    setItems((prev) => prev.map((item, i) =>
      i === index ? { ...item, category: item.category === cat ? 'OTHER' : cat } : item
    ))
  }

  const addItem = () => setItems((prev) => [...prev, { name: '', amount: '', category: 'OTHER' }])

  const removeNewItem = (index: number) => setItems((prev) => prev.filter((_, i) => i !== index))

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')

    if (!purchasedAt) { setError('Date is required'); return }

    const newAmountErrors: Record<number, string> = {}
    items.forEach((item, index) => {
      const v = parseFloat(item.amount)
      if (isNaN(v) || v === 0) newAmountErrors[index] = 'Enter a valid amount'
      else if (v > 9_999_999 || v < -9_999_999) newAmountErrors[index] = 'Amount is too large'
    })
    setAmountErrors(newAmountErrors)
    if (Object.keys(newAmountErrors).length > 0) return

    let finalUpload = uploadResult
    if (receiptFile && !finalUpload) {
      setIsUploading(true)
      try {
        finalUpload = await uploadFile(receiptFile)
        setUploadResult(finalUpload)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to upload receipt')
        return
      } finally {
        setIsUploading(false)
      }
    }

    // Update transaction-level fields
    const txResult = await updateTransaction({
      id: transaction.id,
      merchant: merchant || null,
      purchasedAt,
      notes: notes || null,
    })
    if (txResult.error) { setError(txResult.error.message); return }

    // Was location explicitly cleared by the user?
    const locationCleared = transaction.latitude != null && latitude === null

    for (let i = 0; i < items.length; i++) {
      const item = items[i]
      if (item.id) {
        // Existing item — update in place
        const result = await updateItem({
          input: {
            id: item.id,
            name: item.name.trim() || undefined,
            amount: parseFloat(item.amount),
            category: item.category as never || undefined,
            merchant: merchant || null,
            purchasedAt,
            ...(locationCleared
              ? { clearLocation: true }
              : { latitude, longitude }),
            ...(i === 0 && finalUpload ? {
              receiptUrl: finalUpload.key,
              receiptFilename: finalUpload.filename,
              receiptContentType: finalUpload.contentType,
              receiptSize: finalUpload.size,
            } : {}),
          },
        })
        if (result.error) { setError(result.error.message); return }
      } else {
        // New item — add to existing transaction
        const result = await addItemToTransaction({
          transactionId: transaction.id,
          item: {
            name: item.name.trim(),
            amount: parseFloat(item.amount),
            category: item.category as never || null,
          },
        })
        if (result.error) { setError(result.error.message); return }
      }
    }

    onSuccess()
  }

  return (
    <>
      <div
        className="relative"
        onDragOver={onFormDragOver}
        onDragLeave={onFormDragLeave}
        onDrop={onFormDrop}
      >
        {isDraggingOver && (
          <div className="absolute inset-0 z-10 rounded-lg border-2 border-primary border-dashed bg-primary/5 flex flex-col items-center justify-center gap-2 pointer-events-none">
            <UploadCloud className="h-8 w-8 text-primary" />
            <p className="text-sm font-medium text-primary">Drop to replace receipt</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-3">
          {error && <p className="text-sm text-destructive">{error}</p>}

          {/* Receipt */}
          {currentReceiptUrl && !receiptFile && !isReplacingReceipt ? (
            <div className="border rounded-lg p-3 flex items-center justify-between bg-muted/30">
              <div className="flex items-center gap-2">
                <div className="bg-primary/10 p-2 rounded-md flex-shrink-0">
                  <FileText className="h-4 w-4 text-primary" />
                </div>
                <span className="text-sm text-muted-foreground">Receipt attached</span>
              </div>
              <div className="flex items-center gap-1">
                <a href={currentReceiptUrl} target="_blank" rel="noopener noreferrer">
                  <Button type="button" variant="ghost" size="sm" className="h-7 text-xs">View</Button>
                </a>
                <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setIsReplacingReceipt(true)}>
                  Replace
                </Button>
              </div>
            </div>
          ) : (
            <FileUploader
              value={receiptFile}
              onFileSelect={(f) => { setReceiptFile(f); setUploadResult(null); if (!f) setIsReplacingReceipt(false) }}
            />
          )}

          {/* Merchant + Date */}
          <div className="flex gap-2">
            <Input
              placeholder="Merchant (e.g. Costco)"
              value={merchant}
              onChange={(e) => setMerchant(e.target.value)}
              className="flex-1 h-11 text-sm"
            />
            <DateInput value={purchasedAt} onChange={setPurchasedAt} />
          </div>

          {/* Line items */}
          <div className="space-y-3">
            {items.map((item, index) => (
              <div key={item.id ?? `new-${index}`} className="space-y-1.5 border border-border rounded-lg p-3">
                <div className="flex gap-2 items-center">
                  <Input
                    placeholder="What was purchased?"
                    value={item.name}
                    onChange={(e) => updateField(index, 'name', e.target.value)}
                    className="flex-1 h-10 text-sm"
                    required
                  />
                  <div className="flex flex-col items-end flex-shrink-0">
                    <div className="relative w-28">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm select-none">
                        {currencySymbol}
                      </span>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        value={item.amount}
                        onChange={(e) => updateField(index, 'amount', e.target.value)}
                        className={`pl-7 h-10 text-sm ${amountErrors[index] ? 'border-destructive' : ''}`}
                        required
                      />
                    </div>
                    {amountErrors[index] && (
                      <p className="text-xs text-destructive mt-1">{amountErrors[index]}</p>
                    )}
                  </div>
                  {!item.id && (
                    <button
                      type="button"
                      onClick={() => removeNewItem(index)}
                      className="text-muted-foreground hover:text-destructive transition-colors flex-shrink-0"
                      aria-label="Remove item"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>

                {/* Category chips */}
                <div className="flex flex-wrap gap-1">
                  {Object.values(Category).map((cat) => (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => toggleCategory(index, cat)}
                      className={cn(
                        'flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border transition-colors',
                        item.category === cat
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'border-border text-muted-foreground hover:border-primary/50 hover:text-foreground'
                      )}
                    >
                      <span>{CATEGORY_EMOJIS[cat]}</span>
                      <span>{CATEGORY_DISPLAY[cat]}</span>
                    </button>
                  ))}
                </div>

              </div>
            ))}
          </div>

          {showNotes && (
            <textarea
              placeholder="Add a note…"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none"
            />
          )}

          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={addItem}
              className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <Plus className="h-3.5 w-3.5" />
              Add item
            </button>
            {!showNotes && (
              <button
                type="button"
                onClick={() => setShowNotes(true)}
                className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <Plus className="h-3.5 w-3.5" />
                Add note
              </button>
            )}
          </div>

          {/* Location */}
          <div className="flex items-center gap-2">
            {latitude !== null && longitude !== null ? (
              <span className="flex items-center gap-1 text-xs bg-muted rounded-full px-2.5 py-1">
                <MapPin className="h-3 w-3 flex-shrink-0" />
                <span>Location set</span>
                <button type="button" onClick={() => { setLatitude(null); setLongitude(null) }} className="text-muted-foreground hover:text-destructive flex-shrink-0">
                  <X className="h-3 w-3" />
                </button>
              </span>
            ) : (
              <Button type="button" variant="outline" size="sm" onClick={handleLocation} disabled={isLocating} className="gap-1.5 h-7 text-xs">
                <MapPin className="h-3.5 w-3.5" />
                {isLocating ? 'Locating…' : 'Add location'}
              </Button>
            )}
          </div>

          <div className="flex gap-2">
            <Button type="submit" className="flex-1" disabled={isUploading || saving || deletingTx || items.length === 0}>
              {isUploading ? 'Uploading…' : saving ? 'Saving…' : 'Save changes'}
            </Button>
            <Button type="button" variant="outline" size="icon" className="text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30" onClick={() => setShowDeleteTxConfirm(true)} disabled={saving || deletingTx} title="Delete transaction">
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </form>
      </div>

      {/* Delete transaction confirmation dialog */}
      <Dialog open={showDeleteTxConfirm} onOpenChange={(open) => !open && setShowDeleteTxConfirm(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete transaction</DialogTitle>
            <DialogDescription>
              Delete &quot;{transaction.merchant ?? 'this transaction'}&quot; and all its items? This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteTxConfirm(false)}>Cancel</Button>
            <Button variant="destructive" disabled={deletingTx} onClick={async () => {
              const result = await deleteTransaction({ id: transaction.id })
              if (result.error) { setError(result.error.message); setShowDeleteTxConfirm(false); return }
              setShowDeleteTxConfirm(false)
              onSuccess()
            }}>
              {deletingTx ? 'Deleting…' : 'Delete transaction'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
